import { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api, { formatCurrency, formatDate } from '../../lib/api';
import { toast } from 'sonner';

const AdminPurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    supplier: '',
    product_id: '',
    quantity: '',
    unit_cost: '',
    notes: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [purchasesRes, productsRes] = await Promise.all([
        api.get('/admin/purchases'),
        api.get('/products', { params: { limit: 100 } }),
      ]);
      setPurchases(purchasesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const selectedProduct = products.find(p => p.id === formData.product_id);
    if (!selectedProduct) {
      toast.error('Selecione um produto');
      return;
    }

    const data = {
      supplier: formData.supplier,
      product_id: formData.product_id,
      product_name: selectedProduct.name,
      quantity: parseInt(formData.quantity),
      unit_cost: parseFloat(formData.unit_cost),
      total_cost: parseInt(formData.quantity) * parseFloat(formData.unit_cost),
      notes: formData.notes || null,
    };

    try {
      await api.post('/admin/purchases', data);
      toast.success('Compra registrada! Estoque atualizado.');
      setDialogOpen(false);
      setFormData({
        supplier: '',
        product_id: '',
        quantity: '',
        unit_cost: '',
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar compra');
    }
  };

  const totalCost = formData.quantity && formData.unit_cost 
    ? parseInt(formData.quantity) * parseFloat(formData.unit_cost) 
    : 0;

  return (
    <AdminLayout title="Registro de Compras">
      {/* Header Actions */}
      <div className="flex justify-end mb-6">
        <Button onClick={() => setDialogOpen(true)} className="rounded-xl" data-testid="add-purchase-button">
          <Plus className="w-4 h-4 mr-2" />
          Nova Compra
        </Button>
      </div>

      {/* Purchases Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="purchases-table">
            <thead>
              <tr>
                <th>Fornecedor</th>
                <th>Produto</th>
                <th>Quantidade</th>
                <th>Custo Unit.</th>
                <th>Custo Total</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </td>
                </tr>
              ) : purchases.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhuma compra registrada
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                    <td className="font-medium">{purchase.supplier}</td>
                    <td>{purchase.product_name}</td>
                    <td>{purchase.quantity} un.</td>
                    <td>{formatCurrency(purchase.unit_cost)}</td>
                    <td className="font-medium">{formatCurrency(purchase.total_cost)}</td>
                    <td className="text-muted-foreground text-sm">
                      {formatDate(purchase.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Purchase Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar Nova Compra</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="supplier">Fornecedor *</Label>
              <Input
                id="supplier"
                value={formData.supplier}
                onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
                placeholder="Nome do fornecedor"
                required
                data-testid="purchase-supplier-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product">Produto *</Label>
              <Select
                value={formData.product_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, product_id: value }))}
              >
                <SelectTrigger data-testid="purchase-product-select">
                  <SelectValue placeholder="Selecione o produto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  required
                  data-testid="purchase-quantity-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="unit_cost">Custo Unitário *</Label>
                <Input
                  id="unit_cost"
                  type="number"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: e.target.value }))}
                  required
                  data-testid="purchase-unit-cost-input"
                />
              </div>
            </div>

            {totalCost > 0 && (
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-muted-foreground">Custo Total</p>
                <p className="text-2xl font-bold text-primary" data-testid="purchase-total">
                  {formatCurrency(totalCost)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Nota fiscal, lote, etc."
                data-testid="purchase-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-purchase-button">
                Registrar Compra
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPurchases;
